import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FormBackButton, FormLayoutRenderer, renderField } from '../../../components/generic';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { getLocationReturnPath, useNavigateBack } from '../../../hooks/useNavigateBack';
import { cn } from '../../../lib/utils';
import {
  labelEquipment,
  labelInstance,
  labelJob,
  labelVessel,
  mapItemToFormData,
  moduleConfig,
  relName,
  workOrderEditFields,
  workOrderEditFormLayout,
} from './config';
import { usePmsWorkOrder, type PmsWorkOrder } from './useData';
import { WorkOrderMetaRow, WorkOrderPaneLabel } from './WorkOrderFormChrome';

const WorkOrderEditForm: React.FC = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const location = useLocation();
  const { id: idParam } = useParams<{ id: string }>();
  const editId = idParam != null && /^\d+$/.test(idParam) ? Number(idParam) : NaN;
  const goBack = useNavigateBack(moduleConfig.module.route.list, getLocationReturnPath(location.state));
  const { data, isLoading, mutate } = usePmsWorkOrder(Number.isFinite(editId) ? editId : undefined);
  const formatDate = moduleConfig.utils.formatDate;

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [formDataVersion, setFormDataVersion] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const seededForId = useRef<number | null>(null);

  useEffect(() => {
    if (!data?.id) return;
    if (seededForId.current === data.id) return;
    seededForId.current = data.id;
    const mapped = mapItemToFormData(data) as Record<string, unknown>;
    setFormData(mapped);
    formDataRef.current = mapped;
    setFormDataVersion((v) => v + 1);
  }, [data]);

  const setFieldValue = useCallback((name: string, value: unknown) => {
    let next: unknown = value;
    if (
      typeof value === 'string' &&
      value !== '' &&
      /Id$/.test(name) &&
      /^\d+$/.test(value)
    ) {
      next = Number(value);
    }
    formDataRef.current = { ...formDataRef.current, [name]: next };
    setFormData(formDataRef.current);
    setFormDataVersion((v) => v + 1);
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const nextErr = { ...prev };
      delete nextErr[name];
      return nextErr;
    });
  }, []);

  const setMultipleFieldValues = useCallback((updates: Record<string, unknown>) => {
    formDataRef.current = { ...formDataRef.current, ...updates };
    setFormData(formDataRef.current);
    setTimeout(() => setFormDataVersion((v) => v + 1), 0);
  }, []);

  const getFormData = useCallback(() => formDataRef.current, []);

  const renderedFields = useMemo(() => {
    const out: Record<string, React.ReactNode> = {};
    workOrderEditFields.forEach((field) => {
      out[field.name] = renderField({
        field,
        value: formDataRef.current[field.name],
        onChange: (name, value) => setFieldValue(name, value),
        setFieldValue,
        setMultipleFieldValues,
        getFormData,
        error: formErrors[field.name],
        formKey: `wo-edit-${editId}`,
        formDataVersion,
        isEdit: true,
        formLayoutVariant: 'compact',
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formDataVersion drives refresh of values
  }, [
    editId,
    formDataVersion,
    formErrors,
    getFormData,
    setFieldValue,
    setMultipleFieldValues,
  ]);

  const validate = useCallback(() => {
    const next: Record<string, string> = {};
    workOrderEditFields.forEach((field) => {
      const value = formDataRef.current[field.name];
      const required =
        field.required ||
        (typeof field.validation?.requiredWhen === 'function' &&
          field.validation.requiredWhen(formDataRef.current));
      if (required && (value == null || value === '')) {
        next[field.name] = field.validation?.message || `${field.label} is required`;
      }
    });
    setFormErrors(next);
    return Object.keys(next).length === 0;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || !Number.isFinite(editId)) return;
    if (!validate()) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        ...mapItemToFormData(data),
        ...formDataRef.current,
      };
      await moduleConfig.form.update(editId, payload as Partial<PmsWorkOrder>);
      showSuccess('Work order saved.');
      await mutate();
      goBack();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : moduleConfig.form.errorMessage;
      setError(message || moduleConfig.form.errorMessage);
      showError(message || moduleConfig.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!Number.isFinite(editId)) {
    return (
      <div className="p-6">
        <p className={colors.text}>Invalid work order.</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="large" message={moduleConfig.form.loadingText} />
      </div>
    );
  }

  const woNumber = data.workOrderNumber != null ? String(data.workOrderNumber) : `WO #${data.id}`;
  const woTitle = data.title != null ? String(data.title) : '—';
  const statusName = relName(
    data.status as { name?: string; code?: string } | null,
    data.statusId as number | null,
  );
  const sourceName = relName(
    data.sourceType as { name?: string; code?: string } | null,
    data.sourceTypeId as number | null,
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FormBackButton onClick={goBack} className="mb-2 flex-shrink-0" />

      <form
        onSubmit={handleSubmit}
        className={cn(
          'mx-auto flex w-full max-w-[96rem] min-h-0 flex-1 flex-col overflow-hidden rounded-xl border',
          colors.border,
          colors.surface,
        )}
      >
        {error ? (
          <div className="flex-shrink-0 border-b border-red-200 bg-red-50 px-4 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : null}

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-12">
          <aside
            className={cn(
              'flex min-h-0 flex-col border-b lg:col-span-5 lg:border-b-0 lg:border-r',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className={cn('flex-shrink-0 border-b px-5 py-4', colors.border)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <WorkOrderPaneLabel>Reference</WorkOrderPaneLabel>
                  <p className={cn('mt-1 font-mono text-sm font-semibold tracking-tight', colors.primaryFg)}>
                    {woNumber}
                  </p>
                  <h1 className={cn('mt-1 text-lg font-semibold leading-snug', colors.text)}>
                    {woTitle}
                  </h1>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-md border px-2 py-1 text-xs font-medium',
                    colors.border,
                    colors.surface,
                    colors.text,
                  )}
                >
                  {statusName}
                </span>
              </div>
              <p className={cn('mt-3 text-xs', colors.textSecondary)}>
                Read-only context · opened {formatDate(data.createdAt)}
                {sourceName !== '-' ? ` · via ${sourceName}` : ''}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 dashboard-scrollbar">
              <section>
                <WorkOrderPaneLabel>Scope</WorkOrderPaneLabel>
                <dl className="mt-2">
                  <WorkOrderMetaRow label="Vessel" value={labelVessel(data)} />
                  <WorkOrderMetaRow label="Equipment" value={labelEquipment(data)} />
                  <WorkOrderMetaRow label="Instance" value={labelInstance(data)} />
                  <WorkOrderMetaRow label="Job" value={labelJob(data)} />
                  <WorkOrderMetaRow
                    label="Schedule"
                    value={
                      data.maintenanceScheduleId != null ? `#${data.maintenanceScheduleId}` : '—'
                    }
                  />
                </dl>
              </section>

              <section className="mt-6">
                <WorkOrderPaneLabel>Snapshot at creation</WorkOrderPaneLabel>
                <dl className="mt-2">
                  <WorkOrderMetaRow
                    label="Eq. code"
                    value={
                      data.snapshotEquipmentCode != null
                        ? String(data.snapshotEquipmentCode)
                        : '—'
                    }
                  />
                  <WorkOrderMetaRow
                    label="Eq. name"
                    value={
                      data.snapshotEquipmentName != null
                        ? String(data.snapshotEquipmentName)
                        : '—'
                    }
                  />
                  <WorkOrderMetaRow
                    label="Serial"
                    value={
                      data.snapshotEquipmentSerial != null
                        ? String(data.snapshotEquipmentSerial)
                        : '—'
                    }
                  />
                  <WorkOrderMetaRow
                    label="Job code"
                    value={data.snapshotJobCode != null ? String(data.snapshotJobCode) : '—'}
                  />
                  <WorkOrderMetaRow
                    label="Job title"
                    value={data.snapshotJobTitle != null ? String(data.snapshotJobTitle) : '—'}
                    wide
                  />
                </dl>
              </section>
            </div>
          </aside>

          <div className="flex min-h-0 flex-col lg:col-span-7">
            <div className={cn('flex-shrink-0 border-b px-5 py-4', colors.border)}>
              <WorkOrderPaneLabel>Update</WorkOrderPaneLabel>
              <h2 className={cn('mt-1 text-lg font-semibold', colors.text)}>Work in progress</h2>
              <p className={cn('mt-1 text-sm', colors.textSecondary)}>
                Status, assignment, instructions, and close-out notes.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 dashboard-scrollbar">
              <FormLayoutRenderer
                layout={workOrderEditFormLayout}
                fields={renderedFields}
                formData={formData}
                variant="compact"
              />
            </div>

            <div
              className={cn(
                'flex flex-shrink-0 items-center justify-between gap-3 border-t px-5 py-3',
                colors.border,
                colors.surface,
              )}
            >
              <p className={cn('hidden text-xs sm:block', colors.textMuted)}>
                Left side stays locked for history.
              </p>
              <div className="ml-auto flex items-center gap-2">
                <Button type="button" variant="outline" onClick={goBack}>
                  {moduleConfig.form.cancelText}
                </Button>
                <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                  {moduleConfig.form.edit.submitText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WorkOrderEditForm;
